import { Injectable, PLATFORM_ID, Inject, Optional } from '@angular/core';
import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { TransferState } from '@angular/platform-browser';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { Request } from 'express';
import { Observable, ReplaySubject } from 'rxjs';
import { take, map, tap } from 'rxjs/operators';
import { parseQueryPqrams, removeUrlQueryParam } from 'src/app/util';
import { LoggerService } from './logger.service';
import { ISiteInfo } from '../../web-generator/template-generator/entity/siteInfo';
import { CommonHttpClientService } from './common-http-client.service';
import { GENERATOR_ENV_TOKEN, GeneratorEnv } from '../../web-generator/generator-env-token';
import { CSP_NONCE_KEY } from '../tokens/csp-nonce.statekey';

@Injectable({
  providedIn: 'root'
})
export class ServerConfigService {
  /** 是否為 SSR 模式 */
  isSSRMode = false;
  /** 是否為客戶端渲染 */
  isBrowser: boolean;
  /** 是否為NODE端渲染 */
  isServer: boolean;
  /** 頁面請求時的網址(不根據CSR頁面轉導後更換)，ex:http(s)://hostName:port/baseHref/zh-tw/participate/75?stage=aHR0cCUzQSUyRiUyRmxvY2FsaG9zdCUzQTQzMDA%3D */
  url: string;
  /** 頁面請求時的路徑(不根據CSR頁面轉導後更換)，ex:/baseHref/zh-tw/participate/75 */
  pathname: string;
  /** 頁面請求時的查詢參數(不根據CSR頁面轉導後更換)，ex: {stage: aHR0cCUzQSUyRiUyRmxvY2FsaG9zdCUzQTQzMDA%3D} */
  queryParams: {[key: string]: string};
  /** 是否為編輯模式的 url */
  isEditModeUrl: boolean;
  /** 網站 basehref */
  baseHref: string = '/';
  /** 網站基本設定 */
  private _config: ISiteInfo;
  /** 網站基本設定(唯讀) */
  get config() { return this._config; }
  /** 語系 */
  lang: string;
  /** http://hostName:port */
  origin: string;
  /** 網站基礎設定 */
  protected _config$ = new ReplaySubject<any>();

  constructor(
    @Inject(PLATFORM_ID) protected platformId: object,
    @Optional() @Inject(REQUEST) protected request: Request,
    @Optional() protected tState: TransferState,
    protected http: CommonHttpClientService,
    protected loggerService: LoggerService,
    @Inject(DOCUMENT) protected document: Document,
    @Inject(GENERATOR_ENV_TOKEN) protected generatorEnv: GeneratorEnv,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.isServer = isPlatformServer(this.platformId);
    this.isSSRMode = this.isServer || this.tState.hasKey(CSP_NONCE_KEY);
    if (!this.isBrowser) {
      this.loggerService.info(`isBrowser: ${this.isBrowser}; isServer: ${this.isServer}; isSSRMode: ${this.isSSRMode}`);
    }

    const baseHref = this.document.head.getElementsByTagName('base')[0].getAttribute('href');
    if (baseHref.endsWith('/')) {
      this.baseHref = baseHref.slice(0, baseHref.length - 1)
    } else {
      this.baseHref = baseHref;
    }

    if (this.isBrowser) {
      this.url = location.href.replace('/?', '?').replace(/\/$/g, '');
      this.pathname = removeUrlQueryParam(location.pathname).replace(/\/$/g, '');
      // const queryParam = location.search.slice(1, location.search.length);
      this.queryParams = parseQueryPqrams(this.url);
      this.origin = location.origin;
      window['printAllState'] = () => {
        this.tState.toJson();
      };
    } else {
      this.url = `${this.request.protocol}://${this.request.get('host')}${this.request.originalUrl}`.replace('/?', '?').replace(/\/$/g, '');
      this.loggerService.info(`url: ${this.url}`);
      this.pathname = removeUrlQueryParam(this.request._parsedUrl.pathname).replace(/\/$/g, '');
      this.loggerService.info(`pathname: ${this.pathname}`);
      // const queryParam = this.request._parsedUrl.query;
      this.queryParams = parseQueryPqrams(this.url);
      this.loggerService.info(`queryParams: `, this.queryParams);
      this.origin = `${this.request.protocol}://${this.request.get('host')}`;
    }

    const urlSegArr = this.pathname.split('/');
    const lang = urlSegArr[1];
    this.lang = lang;
    
    this.isEditModeUrl = !!this.queryParams['stage'];
    if (!this.isBrowser) {
      this.loggerService.info(`isEditModeUrl: ${this.isEditModeUrl}`);
    }
  }

  /** 載入網站基本資料 */
  loadConfig$(): Observable<ISiteInfo> {
    // timer(500).pipe(
    //   map(t => siteInfoTestData),
    this.http.get<ISiteInfo>(`${this.generatorEnv.apiHost}/api/site/${this.lang}`).pipe(
      map(info => JSON.parse(JSON.stringify(info))), // 克隆一份為了修正menu的循環引用導致transferState儲存失敗，將transferState裡的物件和實際畫面上使用的資料分開
      tap(info => {
        this._config = info;
      }),
    ).subscribe(config => this._config$.next(config));

    return this.getConfig$();
  }
  
  public getConfig$(): Observable<ISiteInfo> {
    return this._config$.pipe(take(1), map(config => JSON.parse(JSON.stringify(config))));
  }

  getCurrentUrl() {
    if (this.isServer) {
      return this.url;
    } else {
      return location.href;
    }
  }
  
  getCurrentPathname() {
    if (this.isServer) {
      return this.pathname;
    } else {
      return location.pathname;
    }
  }

  getCurrentQueryParams() {
    if (this.isServer) {
      return this.queryParams;
    } else {
      const queryParamStr = location.search.slice(1, location.search.length);
      const queryParams = queryParamStr ? parseQueryPqrams(queryParamStr) : {};
      return queryParams;
    }
  }
}
